// LEAK_PATTERNS must stay in sync with backend/src/common/filters/sanitizing-exception.filter.ts
// Last sync: aebc8254a8b61223dd29188766261a26abdf28bb
//
// Defense-in-depth line #2 (see plan 90-02): the backend filter is the primary
// guard, but if a leak ever slips past it, this client-side helper scrubs the
// raw message and substitutes a safe, context-specific fallback before any
// error string reaches the DOM.

import { emit } from './analytics';

const LEAK_PATTERNS: RegExp[] = [
  /\b[A-Z][A-Z0-9]*(_[A-Z0-9]+){1,}\b/, // ENV_VAR_NAME
  /process\.env\./,
  /\/(Users|home|var|opt|root|etc)\//, // absolute file paths
  /\bat\s+[A-Za-z_$][\w$]*\s*\(/, // stack frame "at Foo ("
  /\bECONN(REFUSED|RESET|ABORTED)\b/,
  /\bEAI_AGAIN\b/,
  /\bER_[A-Z_]+\b/, // MySQL
  /\b(22|23|40|42)[A-Z0-9]{3}\b/, // Postgres SQLSTATE families
  /\benv var\b/i,
];

export interface NormalizedApiError {
  status: number;
  userMessage: string;
  requestId: string | null;
  raw: string | null;
  leaked: boolean;
}

function looksLikeLeak(msg: string): boolean {
  return LEAK_PATTERNS.some((re) => re.test(msg));
}

/**
 * Normalize a backend error response body into a safe, displayable shape.
 *
 * Never pass the `raw` field to the DOM. Always render `userMessage`.
 *
 * @param status     HTTP status code from the response.
 * @param body       Parsed JSON body (may be unknown/malformed).
 * @param fallback   Context-specific message to show when the backend message
 *                   is absent, generic, or looks like a leak.
 */
/**
 * Resolve a stable error_code for PostHog. Prefer the backend's structured
 * `code` field (AppError-derived SEV1/2/3 classes expose it — CLAUDE.md).
 * Fallback to a normalized `HTTP_<status>` so funnel filters still work
 * when the backend omits the field.
 */
function resolveErrorCode(body: unknown, status: number): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    typeof (body as { code?: unknown }).code === 'string' &&
    (body as { code: string }).code.length > 0
  ) {
    return (body as { code: string }).code;
  }
  return `HTTP_${status}`;
}

export function parseApiError(
  status: number,
  body: unknown,
  fallback: string,
): NormalizedApiError {
  const rawMessageCandidate =
    typeof body === 'object' && body !== null && 'message' in body
      ? (body as { message?: unknown }).message
      : undefined;

  const raw =
    typeof rawMessageCandidate === 'string' && rawMessageCandidate.length > 0
      ? rawMessageCandidate
      : null;

  const requestId =
    typeof body === 'object' &&
    body !== null &&
    'requestId' in body &&
    typeof (body as { requestId?: unknown }).requestId === 'string'
      ? (body as { requestId: string }).requestId
      : null;

  // Phase 125-06 — emit `api_error_shown` every time a user-visible error
  // is normalized. parseApiError is the single chokepoint where backend
  // failures become toasts / ErrorBoundary messages, so every emit here
  // corresponds to a user-facing event. Fires BEFORE the return so the
  // event shape is stable across all branches (5xx, 4xx with leak, 4xx
  // clean). The emit() wrapper fails-open per CLAUDE.md.
  emit('api_error_shown', {
    error_code: resolveErrorCode(body, status),
    status_code: status,
  });

  // 5xx: always prefer the caller's fallback. The generic backend 500 message
  // ("Internal server error") is not actionable in any UI context.
  if (status >= 500) {
    if (import.meta.env.DEV && raw) {
      // eslint-disable-next-line no-console
      console.debug('[api-error] 5xx raw message (dev only):', raw, {
        requestId,
      });
    }
    return { status, userMessage: fallback, requestId, raw, leaked: false };
  }

  if (!raw) {
    return {
      status,
      userMessage: fallback,
      requestId,
      raw: null,
      leaked: false,
    };
  }

  if (looksLikeLeak(raw)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[api-error] scrubbed leaky message (dev only):', raw, {
        requestId,
      });
    }
    return { status, userMessage: fallback, requestId, raw, leaked: true };
  }

  return { status, userMessage: raw, requestId, raw, leaked: false };
}
